import { useEffect, useRef, useState } from "react";

import ThumbImg from "../../assets/images/conversations/015.webp";
import PlayIcon  from "../../assets/icons/conversations/play-icon.svg";

/* ─────────────────────────────────────────────
   PREFIX: sb-
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Roboto:wght@400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .sb-page {
    background: #1A1A1A;
    font-family: 'Roboto', sans-serif;
    color: #fff;
  }
  .sb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
  }

  .sb-hero { padding-top: 60px; padding-bottom: 60px; }
  .sb-hero__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
  }
  .sb-hero__left { display: flex; flex-direction: column; gap: 44px; }
  .sb-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .sb-hero__title.sb-visible { opacity: 1; transform: translateY(0); }
  .sb-hero__spotify {
    border-radius: 12px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.18s, transform 0.7s ease 0.18s;
  }
  .sb-hero__spotify.sb-visible { opacity: 1; transform: translateY(0); }
  .sb-hero__spotify iframe {
    display: block; width: 100%; height: 152px; border: none; border-radius: 12px;
  }
  .sb-hero__right {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s;
  }
  .sb-hero__right.sb-visible { opacity: 1; transform: translateY(0); }

  .sb-yt-thumb {
    position: relative; width: 100%; border-radius: 12px;
    overflow: hidden; cursor: pointer; display: block; background: #000;
  }
  .sb-yt-thumb img {
    width: 100%; height: auto; display: block;
    border-radius: 12px; transition: opacity 0.3s ease;
  }
  .sb-yt-thumb:hover img { opacity: 0.85; }
  .sb-yt-thumb__play {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: none; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .sb-yt-thumb__play img { width: 40px; height: 44px; }
  .sb-yt-iframe-wrap {
    position: relative; width: 100%; padding-bottom: 56.25%;
    height: 0; border-radius: 12px; overflow: hidden;
  }
  .sb-yt-iframe-wrap iframe {
    position: absolute; top: 0; left: 0;
    width: 100%; height: 100%; border: none;
  }

  .sb-body {
    padding-bottom: 100px; opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .sb-body.sb-visible { opacity: 1; transform: translateY(0); }
  .sb-body__para {
    font-family: 'Roboto', sans-serif; font-size: 16px;
    font-weight: 400; color: #E2E2E2; line-height: 160%; margin-bottom: 14px;
  }
  .sb-body__para:last-child { margin-bottom: 0; }

  /* ── TABLET (max 768px) ── */
  @media (max-width: 768px) {
    .sb-inner { padding-left: 32px; padding-right: 32px; }
    .sb-hero { padding-top: 32px; padding-bottom: 48px; }
    .sb-hero__grid { grid-template-columns: 1fr 1fr; gap: 24px; }
    .sb-hero__title { font-size: 24px; }
    .sb-hero__left { gap: 16px; }
    .sb-body__para { font-size: 14px; margin-bottom: 12px; }
    .sb-body { padding-bottom: 48px; }
    .sb-yt-thumb__play img { width: 30px; height: 32px; }
  }

  /* ── MOBILE (max 480px) ── */
  @media (max-width: 480px) {
    .sb-inner { padding-left: 16px; padding-right: 16px; }
    .sb-hero { padding-top: 51px; padding-bottom: 30px; }
    .sb-hero__grid { grid-template-columns: 1fr; gap: 30px; }
    .sb-hero__left { gap: 30px; }
    .sb-hero__title { font-size: 30px; }
    .sb-body__para { font-size: 16px; margin-bottom: 10px; }
    .sb-body { padding-bottom: 30px; }
    .sb-yt-thumb__play img { width: 30px; height: 32px; }
  }
`;

function useFadeUp(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("sb-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
}

const YT_VIDEO_ID = "REPLACE_EP15_VIDEO_ID";

function YouTubeEmbed() {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="sb-yt-iframe-wrap">
        <iframe
          src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0`}
          title="EP 15 Sajid Mubashir"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="sb-yt-thumb" onClick={() => setPlaying(true)} role="button" aria-label="Play video">
      <img src={ThumbImg} alt="EP 15 Sajid Mubashir thumbnail" />
      <div className="sb-yt-thumb__play">
        <img src={PlayIcon} alt="Play" />
      </div>
    </div>
  );
}

function HeroSection() {
  const titleRef   = useRef(null);
  const spotifyRef = useRef(null);
  const rightRef   = useRef(null);
  useFadeUp(titleRef);
  useFadeUp(spotifyRef);
  useFadeUp(rightRef);

  return (
    <section className="sb-hero">
      <div className="sb-inner">
        <div className="sb-hero__grid">
          <div className="sb-hero__left">
            <h1 ref={titleRef} className="sb-hero__title">
              EP 15 | Exploring the R&amp;D roadmap of EVs in India | Sajid MUBASHIR | Ministry of Science &amp; Technology
            </h1>
            <div ref={spotifyRef} className="sb-hero__spotify">
              <iframe
                src="https://open.spotify.com/embed/episode/4K2cbV070m3V6e1DM8oL3J?utm_source=generator&theme=0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="EP 15 Spotify Player"
              />
            </div>
          </div>
          <div ref={rightRef} className="sb-hero__right">
            <YouTubeEmbed />
          </div>
        </div>
      </div>
    </section>
  );
}

function BodySection() {
  const ref = useRef(null);
  useFadeUp(ref);
  return (
    <section ref={ref} className="sb-body">
      <div className="sb-inner">
        <p className="sb-body__para">
          In this episode, we address the subject of faster adoption of electric mobility in India from an entirely different perspective. So far, we talked to original equipment manufacturers of both Cars &amp; 2-wheelers, ChargePoint operators, Electricity distribution companies, etc. Today we explore Research &amp; Development &amp; Government Policies in order to achieve the electric dream.
        </p>
        <p className="sb-body__para">
          We speak with Mr. Sajid Mubashir, Chief Scientist with the Department of Science and Technology, Government of India. Sajid has been with the Ministry of Science and Technology since 2016, reporting directly to the principal secretary. Sajid's team actively works on creating a road map for R&amp;D in all sectors of Electric mobility including, battery &amp; charging infrastructure, vehicle system integration, electric components, materials, and manufacturing.
        </p>
      </div>
    </section>
  );
}

export default function Ep15SajidMubashir() {
  return (
    <>
      <style>{CSS}</style>
      <div className="sb-page">
        <HeroSection />
        <BodySection />
      </div>
    </>
  );
}